<template>
  <v-container fluid class="right-container filters-container flex-column">
    <!--div class="fill_width">
      <span class="text-h6">{{ t("common.concepts.filters") }}</span>
      <v-btn icon variant="flat" size="x-small" @click="closeFeature()" color="rgb(var(--v-theme-background))">
        <v-icon>mdi-close</v-icon>
        <v-tooltip activator="parent" location="start">
          {{ t("common.actions.close") }}
        </v-tooltip>
      </v-btn>
    </div>
    <div class="pb-2 text-end">
      <v-btn tile variant="text" class="bgGrey color-red" @click="resetFilters">
        {{ t("common.actions.clearAll") }}
      </v-btn>
    </div-->
    <div v-if="loading" class="d-flex justify-center align-center flex-grow-1">
      <v-progress-circular indeterminate></v-progress-circular>
    </div>
    <div v-for="(filterGroup, index) in filters" v-else :key="index" class="filter_list">
      <v-row class="filter_list__title" justify="space-between">
        <v-col cols="auto">
          <span class="text-h6">{{ filterGroup.displayText.text }}</span>
        </v-col>
        <v-col cols="auto" class="d-flex align-center">
          <v-btn :text="t('common.actions.clearAll')" size="small" variant="outlined" @click="resetFilters"></v-btn>
        </v-col>
      </v-row>
      <template v-for="filter in filterGroup.filters" :key="filter.keyValue">
        <v-row v-if="filter.type !== undefined">
          <v-col width="100%">
            <v-tooltip location="top" activator="parent">
              {{ t('domain.fields.filterField.' + filter.displayText.i18nKey) }}
            </v-tooltip>
            <v-text-field
              v-if="filter.type.inputType === 'text-field'"
              v-model="filter.filterValue"
              :type="filter.type.type"
              variant="outlined"
              :label="t('domain.fields.filterField.' + filter.displayText.i18nKey) ?? filter.displayText.text"></v-text-field>
            <v-autocomplete
              v-if="filter.type.inputType === 'select'"
              v-model="filter.filterValue"
              :items="filter.values"
              :multiple="filter.type.type === 'multiple'"
              variant="outlined"
              :label="t('domain.fields.filterField.' + filter.displayText.i18nKey) ?? filter.displayText.text" />

            <div v-if="filter.type.inputType === 'date'">
              <ZTimeInput
                v-if="filter.type.type === 'time' || filter.type.type === 'datetime'"
                :model-value="dateValue(filter)"
                variant="outlined"
                timezone="Europe/Madrid"
                :label="t('domain.fields.filterField.' + filter.displayText.i18nKey) ?? filter.displayText.text"
                :alone="filter.type.type === 'time'"
                @update:model-value="filter.filterValue = $event"></ZTimeInput>
              <ZDateInput
                v-if="filter.type.type === 'date'"
                :format="tenantDateFormat"
                :model-value="dateValue(filter)"
                variant="outlined"
                timezone="Europe/Madrid"
                :label="t('domain.fields.filterField.' + filter.displayText.i18nKey) ?? filter.displayText.text"
                class="mr-2"
                @update:model-value="filter.filterValue = $event"></ZDateInput>
              <ZDateRangeInput
                v-if="filter.type.type === 'range'"
                :model-value="dateValue(filter)"
                variant="outlined"
                timezone="Europe/Madrid"
                :label="t('domain.fields.filterField.' + filter.displayText.i18nKey) ?? filter.displayText.text"
                class="mr-2"
                @update:model-value="filter.filterValue = $event"></ZDateRangeInput>
            </div>
            <div v-if="filter.type.inputType === 'checkbox'"></div>
          </v-col>
        </v-row>
      </template>
    </div>
    <v-row class="flex-grow-0 action-buttons">
      <v-col>
        <v-btn class="cancel-button" block size="small" variant="outlined" @click="closeFeature()">
          {{ t('common.actions.close') }}
        </v-btn>
      </v-col>
      <v-col>
        <v-btn block color="primary" size="small" variant="flat" @click="apply()">
          {{ t('common.actions.apply') }}
        </v-btn>
      </v-col>
    </v-row>
  </v-container>
</template>
<script setup lang="ts">
import type { Ref } from 'vue'
import { onMounted, ref } from 'vue'

import { storeToRefs } from 'pinia'

import { useI18n } from 'vue-i18n'

import { getFilters } from '@/services/api/filters.ts'
import FiltersStore from '@/stores/FiltersStore'
import SettingsStore from '@/stores/settingsStore'
import { filterTypes } from '@/types/Filters'
import type { FilterGroup, Filter } from '@/types/Filters'

const filtersStore = FiltersStore()
// undefined keeps the component's own default; only a tenant with a fixed format overrides it.
// Not given to ZDateRangeInput: typing a range in a non-ISO format throws in @tsyl/zapan2 0.10.25
const { tenantDateFormat } = storeToRefs(SettingsStore())
const { filtersOpen, filters: storeFilters, initialFilters } = storeToRefs(filtersStore)

const { t } = useI18n()

const filters: Ref<FilterGroup[]> = ref([])
const loading = ref(false)

// Date and time filters always hold a single string; filterValue is typed string | string[] for the select filters
function dateValue(filter: Filter) {
  return filter.filterValue as string | undefined
}

const orderFunction: (a: FilterGroup | Filter, b: FilterGroup | Filter) => number = (a, b) => a.displayOrder - b.displayOrder

async function getAndBuildFilters() {
  loading.value = true
  if (!initialFilters.value) {
    const incidentFilters = await getFilters()
    incidentFilters.map((group: FilterGroup) => {
      group.filters.map((filter: Filter) => {
        filter.filterValue = undefined
        filter.type = filterTypes.find((x) => x.id === filter.filterId)
        return filter
      })
      return group
    })
    filters.value = incidentFilters
      .map((element: FilterGroup) => {
        element.filters = element.filters.sort(orderFunction)
        return element
      })
      .sort(orderFunction)
    initialFilters.value = filters.value
  } else {
    filters.value = initialFilters.value
  }
  loading.value = false
}

function apply() {
  storeFilters.value = filters.value[0].filters.slice()
}

function closeFeature() {
  filtersOpen.value = false
}

function resetFilters() {
  initialFilters.value = null
  storeFilters.value = []
  getAndBuildFilters()
}

onMounted(async () => await getAndBuildFilters())
</script>

<style lang="scss" scoped>
.right-container {
  background-color: rgb(var(--v-theme-background)) !important;
}

.fill_width {
  width: 100% !important;
}

.filter_list {
  width: 100% !important;
  overflow-y: auto;
  overflow-x: hidden;
  height: calc(100vh - 100px);
  background-color: rgb(var(--v-theme-background));
  /*&__title {
    position: sticky;
    top: 0;
  }*/
}

.slide-right-enter-active,
.slide-right-leave-active {
  transition: margin 0.3s ease;
}

.slide-right-enter-from,
.slide-right-leave-to {
  margin-right: calc(var(--right-panel-width) * -1);
}

.filters-container {
  width: var(--right-panel-width);
}

.action-buttons {
  background-color: rgb(var(--v-theme-surface));
  position: absolute;
  bottom: 0;
  width: var(--right-panel-width);
  padding-bottom: 10px;
  background-color: rgb(var(--v-theme-surface-light));
}

.cancel-button {
  background-color: rgb(var(--v-theme-surface));
}
</style>
